import { IComboBox, Stack } from '@fluentui/react';
import { IComboBoxOption } from '@fluentui/react';
import { ComboBox } from '@h2oai/ui-kit';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { CloudPlatformDiscoveryConfig } from '../../../../utils/contexts';
import { CloudDiscoveryKey } from '../../../../utils/contexts';
import { useCloudPlatformDiscovery } from '../../../../utils/hooks';
import { ComponentFilterProps } from './types';

export const auditTrailComponentFilterKey = 'audit-trail-component-filter';

type AuditTrailComponentOption = IComboBoxOption & {
  data: { discoveryKey?: CloudDiscoveryKey };
};

const componentOptions: AuditTrailComponentOption[] = [
  {
    key: 'h2oai-enginemanager-.*',
    text: 'Engine Manager',
    data: { discoveryKey: CloudDiscoveryKey.aiEngineManagerApiUrl },
  },
  { key: 'h2oai-securestore-.*', text: 'Secure Store', data: { discoveryKey: CloudDiscoveryKey.secureStore } },
  { key: 'h2oai-audittrail-.*', text: 'Audit Trail', data: { discoveryKey: CloudDiscoveryKey.auditTrailApiUrl } },
  { key: 'h2oai-authzserver-.*', text: 'AuthZ', data: { discoveryKey: CloudDiscoveryKey.authzUrl } },
  { key: 'h2oai-driverlessai-.*', text: 'Driverless AI', data: {} }, // Always On
  {
    key: '(h2oai-mlops-|h2oai-modelscoring-|h2oai-modeldeployment-|h2oai-modelstorage-|h2oai-modelmonitoring-).*',
    text: 'MLOps',
    data: { discoveryKey: CloudDiscoveryKey.mlopsApiUrl },
  },
  { key: 'h2oai-tokenserver-.*', text: 'Token Server', data: {} },
  { key: 'h2oai-drive-.*', text: 'Drive', data: { discoveryKey: CloudDiscoveryKey.workspaceDriveUrl } },
  { key: 'h2oai-modelhub-.*', text: 'Model Hub', data: { discoveryKey: CloudDiscoveryKey.modelHub } },
  { key: 'thirdparty-iamguarded-keycloak', text: 'Keycloak', data: {} }, // Always On
];

export const ComponentFilter = memo((props: ComponentFilterProps) => {
  const services = useCloudPlatformDiscovery() as Partial<CloudPlatformDiscoveryConfig> | undefined;
  const { onChange, value } = props;
  const comboboxRef = useRef<IComboBox>(null);
  const hasInitialized = useRef(false);

  const filteredOptions = useMemo<AuditTrailComponentOption[]>(() => {
    return componentOptions.filter((opt): opt is AuditTrailComponentOption => {
      const discoveryKey = opt.data.discoveryKey as keyof CloudPlatformDiscoveryConfig | undefined;
      if (!discoveryKey) return true;
      const discoveredValue = services?.[discoveryKey];
      return Boolean(discoveredValue);
    });
  }, [services]);

  useEffect(() => {
    if (value && !hasInitialized.current) {
      const option = filteredOptions.find((opt) => opt.key === value);
      if (option) {
        onChange(value, option.text);
      }
      hasInitialized.current = true;
    }
  }, [value, onChange, filteredOptions]);

  const handleClick = useCallback(() => {
    comboboxRef.current?.focus(true);
  }, []);

  const handleChange = useCallback(
    (_e: any, option?: IComboBoxOption) => {
      const selectedKey = option?.key as string | undefined;
      const readableValue = option?.text || '';
      onChange(selectedKey, readableValue);
    },
    [onChange]
  );

  const selectedOptionText = filteredOptions.find((opt) => opt.key === value)?.text || '';

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
      <ComboBox
        text={selectedOptionText}
        label="Component Filter"
        useComboBoxAsMenuWidth
        selectedKey={value}
        placeholder="Select filter"
        options={filteredOptions}
        onChange={handleChange}
        componentRef={comboboxRef}
        onClick={handleClick}
      />
    </Stack>
  );
});
